import RvApi from "@/rvapi";
import { defineStore } from "pinia";
import constant from "@/constant";
import type { MailOutOrderListRequest, MailOutState } from "@/types/mailout";

const { CREATE_MAILOUT } = constant;

export const useMailOutStore = defineStore("mailout", {
  state: (): MailOutState => ({
    orders: [],
    isLoading: false,
    error: null,
    currentOrder: null,
    isLoadingDetail: false,
    currentPage: CREATE_MAILOUT.PAGE_NUMBER,
    pageSize: CREATE_MAILOUT.PAGE_LIMIT,
    totalPages: 1,
    selectedStatus: "",
    searchTerm: "",
    emailSearchTerm: "",
  }),
  actions: {
    buildOrdersPayload(page: number): MailOutOrderListRequest {
      const payload: MailOutOrderListRequest = {
        productId: CREATE_MAILOUT.PRODUCT_ID,
        page,
        pageSize: this.pageSize,
      };
      const trimmedSearchTerm = this.searchTerm.trim();
      const trimmedEmailSearchTerm = this.emailSearchTerm.trim();
      if (this.selectedStatus) {
        payload.status = this.selectedStatus;
      }
      if (trimmedSearchTerm) {
        payload.searchKey = trimmedSearchTerm;
      }
      if (trimmedEmailSearchTerm) {
        payload.email = trimmedEmailSearchTerm;
      }
      return payload;
    },
    async fetchOrders(page = CREATE_MAILOUT.PAGE_NUMBER) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await RvApi.getOrderList(
          this.buildOrdersPayload(page),
        );
        this.orders = response.orders ?? [];
        this.currentPage = response.pagination?.page ?? response.page ?? page;
        if (
          response.pagination?.totalPages &&
          response.pagination.totalPages > 0
        ) {
          this.totalPages = response.pagination.totalPages;
        } else if (response.totalPages && response.totalPages > 0) {
          this.totalPages = response.totalPages;
        } else if (typeof response.total === "number") {
          const hasMorePages = this.orders.length === this.pageSize;
          this.totalPages = Math.max(
            this.currentPage,
            Math.ceil(response.total / this.pageSize),
            hasMorePages ? this.currentPage + 1 : this.currentPage,
          );
        } else {
          this.totalPages = Math.max(
            this.currentPage,
            this.orders.length === this.pageSize
              ? this.currentPage + 1
              : this.currentPage,
          );
        }
      } catch {
        this.error = "Failed to fetch orders. Please try again.";
        this.orders = [];
        this.totalPages = 1;
      } finally {
        this.isLoading = false;
      }
    },

    async updateStatusFilter(status: string) {
      this.selectedStatus = status;
      await this.fetchOrders(CREATE_MAILOUT.PAGE_NUMBER);
    },
    async updateSearch(searchTerm: string) {
      this.searchTerm = searchTerm;
      await this.fetchOrders(CREATE_MAILOUT.PAGE_NUMBER);
    },
    async updateEmailSearch(emailSearchTerm: string) {
      this.emailSearchTerm = emailSearchTerm;
      await this.fetchOrders(CREATE_MAILOUT.PAGE_NUMBER);
    },
    clearError() {
      this.error = null;
    },
    async fetchOrderDetail(orderId: string) {
      this.isLoadingDetail = true;
      this.error = null;
      this.currentOrder = null;
      try {
        const response = await RvApi.getMailOutOrderDetail(orderId);
        const files = response.files ?? {};
        const urls = Object.entries(files)
          .filter(([, value]) => typeof value === "string" && value)
          .map(([filename, url]) => ({
            filename,
            url: String(url),
            modified: response.updatedOn ?? response.orderDate,
          }));

        this.currentOrder = {
          ...response,
          userId: response.userId,
          urls,
        };
      } catch {
        this.error = "Failed to fetch order details. Please try again.";
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async sendToPrinting(orderId: string) {
      await RvApi.reuploadMailOutOrder(orderId);
    },
    async cancelOrder(orderId: string, reason?: string) {
      await RvApi.cancelMailOutOrder(orderId, reason);
    },
    async resendReceipt(orderId: string, email: string) {
      await RvApi.resendMailOutReceipt(orderId, email);
    },
  },
});
